import type { ganttCopy } from '../pt/gantt';

/**
 * Español (neutro, LatAm) — cronograma (Gantt), grade de tarefas e colunas.
 *
 * Sem "vosotros": o imperativo é sempre a forma de "tú" ("Arrastra", "Agrega",
 * "Selecciona"), que serve México, Colômbia e Cone Sul sem soar peninsular.
 *
 * Vocabulário de gestão de projetos hispanofalante: "ruta crítica",
 * "predecesoras", "sangría", "hito". "Baseline", "Backlog" e "Sprint" ficam em
 * inglês, como no resto do produto e na literatura que o usuário lê fora dele.
 *
 * A máscara de data continua dd/mm/aaaa porque `parseDateInput` só aceita
 * dia/mês/ano — é o mesmo formato que a LatAm já escreve.
 */
const gantt: ganttCopy = {
  module: {
    filterAssigned: 'Responsable',
    filterTitle: 'Filtrar por responsable',
    clear: 'Limpiar',
    clearFilters: 'Limpiar filtros',
    filterStats: '{{shown}} de {{total}} tareas',

    baseline: 'Baseline',
    baselineSave: 'Guardar Baseline',
    baselineSaved: '¡Baseline guardado!',
    baselineShow: 'Mostrar Baseline',
    baselineHide: 'Ocultar Baseline',
    baselineClear: 'Limpiar Baseline',
    baselineCleared: 'Baseline eliminado.',
    baselineClearConfirm: '¿Eliminar la línea base? Se perderá el plan original guardado para comparación.',

    criticalPath: 'Ruta Crítica',
    criticalPathActive: 'Ruta Crítica activa',
    criticalPathOff: 'Desactivar Ruta Crítica',
    cpCritical: { zero: 'críticas', one: 'crítica', other: 'críticas' },
    cpNearCritical: { zero: 'sub-críticas', one: 'sub-crítica', other: 'sub-críticas' },
    cpTask: { zero: 'tareas', one: 'tarea', other: 'tareas' },
    cpOfTotal: 'de {{total}} {{tasks}}',
    cpLegendCritical: 'crítica',
    cpLegendNear: 'sub-crítica',

    resizeHint: 'Arrastra para redimensionar',
    addTask: 'Agregar tarea',

    empty: {
      title: 'Tu cronograma está vacío',
      description: 'Agrega tu primera tarea para empezar a planificar el proyecto. También puedes pegar tareas de una hoja de cálculo con Ctrl+V.',
      action: 'Agregar Primera Tarea',
      readonlyTitle: 'Este proyecto aún no tiene tareas',
      readonlyDescription: 'Espera a que el responsable agregue tareas al cronograma.',
    },
  },

  /**
   * O desenho do gráfico. "Holgura" é o termo hispanofalante para folga, o
   * mesmo que a literatura de ruta crítica usa.
   */
  chart: {
    today: 'Hoy',
    weekShort: 'Sem {{number}}',
    criticalTask: 'Tarea crítica',
    nearCritical: 'Sub-crítica',
    epic: 'Épico (fechas calculadas)',
    criticalWarning: 'Retrasar esta tarea aplaza la fecha de término del proyecto.',
    float: 'Holgura: {{days}} {{unit}}',
    dayUnit: { zero: 'días', one: 'día', other: 'días' },
    baselineOnTime: 'Baseline: a tiempo',
    baselineVariance: 'Baseline: {{delta}} días',
    ariaCritical: '{{task}}, tarea crítica, holgura 0 días',
    ariaNearCritical: '{{task}}, sub-crítica, holgura {{days}} días',
  },

  table: {
    selected: { one: '1 tarea seleccionada', other: '{{count}} tareas seleccionadas' },
    indent: 'Aumentar sangría',
    outdent: 'Reducir sangría',
    link: 'Encadenar',
    linkTooltip: 'Encadenar las tareas seleccionadas como predecesoras',
    deleteSelected: 'Eliminar {{count}}',
    clear: 'Limpiar',
    selectionHint: 'Tab/Shift+Tab para sangría | Esc para limpiar',

    tableOptions: 'Opciones de la tabla',
    addColumn: 'Agregar columna',
    manageColumns: 'Gestionar columnas',
    manageColumnsTitle: 'Gestionar columnas visibles',

    addTask: 'Agregar tarea',
    pasteHint: 'Pegar tareas (Ctrl+V)',
    pasteAdded: { one: '{{count}} tarea agregada', other: '{{count}} tareas agregadas' },

    confirmDeleteTitle: 'Eliminar tarea',
    confirmDeleteMessage: '¿Eliminar la tarea seleccionada? Esta acción no se puede deshacer.',
    confirmDeleteLabel: 'Eliminar',

    openDetails: 'Abrir detalles de la tarea',
  },

  header: {
    edit: 'Editar columna',
    hide: 'Ocultar columna',
    show: 'Mostrar columna',
    delete: 'Eliminar columna',
  },

  config: {
    editTitle: 'Editar columna',
    newTitle: 'Nueva columna',
    name: 'Nombre',
    namePlaceholder: 'Ingresa un nombre de columna',
    type: 'Tipo de columna',
    systemColumn: 'Esta es una columna del sistema. El tipo no se puede cambiar.',
    currentType: 'Tipo actual:',
    listOptions: 'Opciones de la lista',
    optionPlaceholder: 'Agregar opción',
    addOption: 'Agregar',
    symbolSet: 'Conjunto de símbolos',
    symbolSetPlaceholder: 'Selecciona el conjunto',
    symbolSets: {
      priority: 'Prioridad',
      status: 'Estado',
      flags: 'Banderas',
    },
    readonlyNote: 'Esta columna es de solo lectura',
    cancel: 'Cancelar',
    save: 'Guardar',
    create: 'Ok',
    typeChangeTitle: 'Cambiar el tipo de la columna',
    typeChangeMessage: 'Al cambiar el tipo de la columna, los datos existentes pueden perderse o mostrarse de forma incorrecta. Por ejemplo, si la columna contiene texto y lo cambias a "Fecha", los valores de texto no se van a convertir. ¿Deseas continuar?',
    typeChangeConfirm: 'Continuar',
  },

  visibility: {
    title: 'Columnas visibles',
    system: 'Sistema',
    lastVisible: 'Al menos una columna debe estar visible',
    resetWarning: 'Esto restaura todas las columnas al valor original. Las columnas personalizadas se eliminan.',
    cancel: 'Cancelar',
    confirm: 'Confirmar',
    reset: 'Restaurar predeterminado',
  },

  comment: {
    title: 'Nota',
    placeholder: 'Escribe tu nota aquí...',
    saveHintPrefix: 'Presiona',
    saveHintSuffix: 'para guardar',
    cancel: 'Cancelar',
    save: 'Guardar',
  },

  cell: {
    taskNamePlaceholder: 'Nombre de la tarea',
    calculated: 'Calculado automáticamente desde las subtareas',
    computedValue: 'Valor calculado',
    readonly: 'Solo lectura',
    hoursPlaceholder: '0h',
    costPending: 'Costo calculado — disponible en PR 6',
    epicNoPredecessors: 'Los épicos no pueden tener predecesoras. Las fechas se calculan automáticamente a partir de las subtareas.',
    epicNoPredecessorsShort: 'Los épicos no pueden tener predecesoras - las fechas se calculan desde las subtareas',
    autoBadge: 'Auto',
    autoTooltip: 'Auto-calculado',
    backlog: 'Backlog',
    selectPlaceholder: 'Selecciona',
    addComment: 'Haz clic para agregar una nota',
    addPlaceholder: 'Agregar...',
    addDescription: 'Haz clic para agregar una descripción',
    descriptionPlaceholder: 'Describe la tarea...',

    selectEmpty: 'Selecciona...',
    datePlaceholder: 'dd/mm/aaaa',
    editHint: 'Doble clic para editar',

    /** Columna de conversación (task_comments, el feed real con autor y notificaciones). */
    conversationEmpty: 'Sin comentarios. Haz clic para iniciar la conversación.',
    conversationLoading: 'Cargando comentarios...',
    conversationHint: 'Haz clic para abrir la conversación',
  },

  contextMenu: {
    openDetails: 'Abrir detalles',
    formatting: 'Formato del texto',
    bold: 'Negrita',
    italic: 'Cursiva',
    underline: 'Subrayado',
    addChild: 'Agregar subtarea',
    addAbove: 'Agregar tarea arriba',
    addBelow: 'Agregar tarea abajo',
    duplicate: 'Duplicar',
    moveUp: 'Mover hacia arriba',
    moveDown: 'Mover hacia abajo',
    indent: 'Aumentar sangría',
    outdent: 'Reducir sangría',
    dependencies: 'Gestionar dependencias',
    dependenciesLocked: 'Disponible en el plan Starter',
    highlight: 'Resaltar la fila',
    highlightSelected: 'Resaltar {{count}} seleccionadas',
    deleteSelected: 'Eliminar {{count}} seleccionadas',
    delete: 'Eliminar',
  },

  dependencies: {
    title: 'Gestionar Dependencias',
    taskLabel: 'Tarea:',
    current: 'Dependencias actuales',
    emptyCurrent: 'Ninguna dependencia seleccionada',
    lagTooltip: 'Retraso: +2d = 2 días después, -1d = 1 día antes',
    lagHint: 'Retraso (lag): +2d = inicia 2 días después, -1d = inicia 1 día antes del fin de la predecesora',
    available: 'Tareas disponibles',
    availableHint: 'Selecciona las tareas que deben completarse antes de esta',
    emptyAvailable: 'Ninguna tarea disponible',
    cancel: 'Cancelar',
    save: 'Guardar',
  },

  columns: {
    rowNumber: '#',
    name: 'Nombre de la tarea',
    conversation: 'Conversación',
    description: 'Descripción',
    predecessors: 'Predecesoras',
    duration: 'Duración',
    startDate: 'Fecha de inicio',
    endDate: 'Fecha de término',
    assigned: 'Asignado a',
    sprint_id: 'Sprint',
    priority: 'Prioridad',
    progress: 'Progreso',
    status: 'Estado',
    estimated_hours: 'Estimado (h)',
    actual_hours: 'Real (h)',
    remaining_hours: 'Restante (h)',
    baseline_estimated_hours: 'Estimado Original (h)',
    hours_variance: 'Variación (h)',
    planned_cost: 'Costo Planificado',
    actual_cost: 'Costo Real',
  },

  columnTypes: {
    text: { name: 'Texto/número', description: 'Campo de texto o número libre' },
    select: { name: 'Lista desplegable', description: 'Lista de opciones predefinidas' },
    date: { name: 'Fecha', description: 'Selector de fecha' },
    duration: { name: 'Duración', description: 'Duración en días, semanas, etc.' },
    hours: { name: 'Horas', description: 'Valor en horas (estimado, real, restante, baseline)' },
    money: { name: 'Moneda (R$)', description: 'Valor monetario derivado de horas × tarifa' },
    people: { name: 'Lista de contactos', description: 'Miembros del equipo del proyecto' },
    checkbox: { name: 'Casilla de verificación', description: 'Marcado o desmarcado' },
    symbols: { name: 'Símbolos', description: 'Símbolos de prioridad o estado' },
    autonumber: { name: 'Autonumeración', description: 'Número secuencial automático' },
    // "Nota" y no "Comentario": el comentario real es el feed de task_comments
    // (columna Conversación); este tipo es texto libre sin autor ni historial.
    comment: { name: 'Nota', description: 'Campo de nota libre (sin historial ni notificaciones)' },
    progress: { name: 'Progreso', description: 'Barra de progreso 0-100%' },
    status: { name: 'Estado', description: 'Estado de la tarea (auto-calculado o manual)' },
    sprint: { name: 'Sprint', description: 'Sprint del proyecto' },
    description: { name: 'Descripción', description: 'Descripción detallada de la tarea (rich text)' },
  },

  status: {
    not_started: 'No iniciado',
    in_progress: 'En curso',
    completed: 'Completado',
    paused: 'Pausado',
    blocked: 'Bloqueado',
  },

  priority: {
    none: '—',
    low: 'Baja',
    medium: 'Media',
    high: 'Alta',
  },

  symbols: {
    priority: {
      critical: 'Crítico',
      high: 'Alta',
      medium: 'Media',
      low: 'Baja',
      none: 'Ninguna',
    },
    status: {
      not_started: 'No iniciado',
      in_progress: 'En curso',
      blocked: 'Bloqueado',
      completed: 'Completado',
    },
    flags: {
      flag_red: 'Bandera roja',
      flag_yellow: 'Bandera amarilla',
      flag_green: 'Bandera verde',
      none: 'Sin bandera',
    },
  },

  highlightColors: {
    none: 'Sin color',
    yellow: 'Amarillo',
    pink: 'Rosa',
    blue: 'Azul',
    green: 'Verde',
    purple: 'Morado',
    orange: 'Naranja',
    gray: 'Gris',
  },
};

export default gantt;
